// THE FACE A BODY WEARS — the World's half of it.
//
// Khari, 2026-08-31: *"turn on the video faces, and make sure they animate when
// the avatar is chatting/talking."*
//
// A built body carries a generated mouth loop and the numbers describing where
// on its head that loop is projected (`pose.face`, written by the website's
// Model Lab). Turning that into what `AnimatedResident` mounts is these few
// lines. The Web repo has the same function — it is small, it is the ADAPTER
// rather than the algorithm (which is `faceDecal.js`, copied and guarded), and
// the two differ where they should: the Web one resolves through its own
// `apiUrl`, this one through the World's `API_BASE`.
//
// ⚠ "IF AVAILABLE" MEANS A `video`, NOT A `face`. The box numbers can sit on a
// body whose loop was never generated, and a decal with no source renders the
// DEBUG COLOUR — a flat patch across the cheek, which reads as a broken model
// rather than as "no clip yet". No video, no decal.
//
// ⚠ `speaking` DOES NOT MEAN "SHOWN". It means the loop is PLAYING. The decal
// is visible from the moment it is cut and never leaves — paused, it holds the
// plate the body was built from, which IS the artist's resting face, so nothing
// swaps when they start talking. Passing `false` costs nothing and saves a
// video decoding every frame for a body nobody is talking to, which in a room
// of twelve residents is the whole ball game.
import { API_BASE } from '../../config';

/**
 * `pose.face` → what `AnimatedResident` mounts, or null when there is nothing
 * to show. `speaking` is whether the mouth should be MOVING right now.
 */
export function facePanelFor(pose, { speaking = false } = {}) {
  const face = pose?.face;
  if (!face?.video) return null;
  return {
    ...face,
    /* ⚠ The body stores a PATH on the API's asset proxy, not an absolute URL —
       the same rule `rigged`/`walk`/`idle` follow. Absolutised here as well as
       in `api.js` on purpose: this is idempotent, and a pose that reached a
       caller by some other road still has to play. */
    src: face.video.startsWith('/') ? `${API_BASE}${face.video}` : face.video,
    debug: false,
    speaking,
  };
}

/**
 * What CHANGES the cut, as a string — so a body does not re-cut its face every
 * render. Deliberately EXCLUDES `speaking`, which flips on every turn of a
 * conversation and must never rebuild the patch; starting and stopping the clip
 * is a separate, cheap call.
 */
export function facePanelKey(panel) {
  if (!panel) return '';
  const { speaking, ...rest } = panel;
  return JSON.stringify(rest);
}

export default facePanelFor;
